import React, { useMemo, useRef, useState } from "react";
import { CheckCircle2, FileSpreadsheet, Loader2, TriangleAlert, Upload, X } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";

function ExcelImportPage({
  title,
  endpoint,
  fileFieldName = "file",
  description,
  requiredColumns = [],
  extraFields = [],
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const acceptedTypes = ".xlsx,.xls";
  const inputId = `excel-upload-${fileFieldName.replace(/[^a-z0-9_-]/gi, "")}`;

  const helperText = useMemo(() => {
    if (!requiredColumns.length) return null;
    return requiredColumns.join(" | ");
  }, [requiredColumns]);

  const setNextFile = (nextFile) => {
    setFile(nextFile);
    setResult(null);
  };

  const handleFileChange = (event) => {
    setNextFile(event.target.files?.[0] || null);
  };

  const resetFile = () => {
    setFile(null);
    setResult(null);
    setIsDragging(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const droppedFile = event.dataTransfer?.files?.[0] || null;
    if (droppedFile) {
      setNextFile(droppedFile);
    }
  };

  const getErrorMessage = (error) => {
    const data = error?.response?.data;
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.error === "string") return data.error;
    if (typeof data?.message === "string") return data.message;
    if (!error?.response) return "ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی و دوباره تلاش کنید.";
    return "بارگذاری فایل انجام نشد. دوباره تلاش کنید.";
  };

  const getSuccessMessage = (data) => {
    if (typeof data?.success === "string") return data.success;
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
    return "درون‌ریزی با موفقیت انجام شد.";
  };

  const getSuccessMeta = (data) => {
    if (typeof data?.imported_count === "number") {
      return `تعداد ردیف‌های پردازش‌شده: ${data.imported_count}`;
    }
    return null;
  };

  const getSuccessWarnings = (data) => {
    if (Array.isArray(data?.warnings) && data.warnings.length) {
      return data.warnings;
    }
    return [];
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setResult({
        type: "error",
        message: "ابتدا فایل اکسل را انتخاب کنید.",
      });
      return;
    }

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setResult({ type: "error", message: "فقط فایل اکسل با پسوند .xlsx یا .xls پذیرفته می‌شود." });
      return;
    }

    const formData = new FormData();
    formData.append(fileFieldName, file);
    extraFields.forEach((field) => {
      formData.append(field.name, field.value);
    });

    try {
      setIsUploading(true);
      setResult(null);

      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResult({
        type: "success",
        message: getSuccessMessage(response.data),
        meta: getSuccessMeta(response.data),
        warnings: getSuccessWarnings(response.data),
      });
    } catch (error) {
      setResult({
        type: "error",
        message: getErrorMessage(error),
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 px-4 pb-6 pt-24 md:px-6 md:pb-10 md:pt-28">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1.15fr)_360px]">
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-5 md:px-7">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <FileSpreadsheet className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl font-semibold text-slate-900 md:text-3xl">{title}</h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  {description}
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-5 py-5 md:px-7 md:py-6">
            <label
              htmlFor={inputId}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed px-6 py-10 text-center transition ${
                isDragging
                  ? "border-emerald-500 bg-emerald-50"
                  : "border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50/40"
              }`}
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm">
                <Upload className="h-6 w-6" />
              </div>
              <div className="mt-4 text-base font-medium text-slate-900">
                {file ? "برای جایگزینی، فایل دیگری انتخاب یا رها کنید" : "فایل اکسل را انتخاب یا اینجا رها کنید"}
              </div>
              <div className="mt-1 text-sm text-slate-500">فرمت‌های قابل قبول: `.xlsx` و `.xls`</div>
              <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={acceptedTypes}
                onChange={handleFileChange}
                className="sr-only"
              />
            </label>

            <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-medium text-slate-700">فایل انتخاب‌شده</div>
              {file ? (
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-slate-900">{file.name}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {`${(file.size / 1024 / 1024).toFixed(2)} مگابایت`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetFile}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700 hover:bg-white"
                  >
                    <X className="h-4 w-4" />
                    حذف فایل
                  </button>
                </div>
              ) : (
                <div className="mt-2 text-sm text-slate-500">هنوز فایلی انتخاب نشده است.</div>
              )}
            </div>

            {extraFields.length > 0 && (
              <div className="mt-5 space-y-3">
                {extraFields.map((field) => {
                  if (field.type !== "checkbox") return null;

                  return (
                    <label
                      key={field.name}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(field.checked)}
                        onChange={(event) => field.onChange(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="leading-6">{field.label}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {result && (
              <div
                role={result.type === "error" ? "alert" : "status"}
                className={`mt-5 rounded-lg border px-4 py-3 text-sm ${
                  result.type === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`}
              >
                <div className="flex items-start gap-2">
                  {result.type === "success" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <div>
                    <div>{result.message}</div>
                    {result.meta && <div className="mt-1 text-xs opacity-80">{result.meta}</div>}
                    {Array.isArray(result.warnings) && result.warnings.length > 0 && (
                      <ul className="mt-2 list-disc pr-5 text-xs leading-5">
                        {result.warnings.slice(0, 10).map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                        {result.warnings.length > 10 && (
                          <li>{`${result.warnings.length - 10} هشدار دیگر...`}</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs leading-5 text-slate-500">
                بسته به منطق این مسیر، رکوردهای موجود ممکن است به‌روزرسانی شوند.
              </div>
              <button
                type="submit"
                disabled={isUploading}
                className="inline-flex h-11 min-w-[160px] items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    در حال بارگذاری
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    شروع درون‌ریزی
                  </>
                )}
              </button>
            </div>
          </form>
        </section>

        <aside className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">ستون‌های مورد انتظار</h2>
            {helperText ? (
              <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm leading-6 text-slate-600">
                {helperText}
              </div>
            ) : (
              <p className="mt-3 text-sm leading-6 text-slate-500">این درون‌ریزی از ساختار استاندارد فایل اکسل استفاده می‌کند.</p>
            )}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">پیش از بارگذاری</h2>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-600">
              <li>از شیت اول فایل استفاده کنید.</li>
              <li>ردیف عنوان ستون‌ها را بدون تغییر نگه دارید.</li>
              <li>تا حد ممکن از سلول‌های ساده به‌جای سلول‌های ادغام‌شده استفاده کنید.</li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default ExcelImportPage;
